// Volume Rendering folder builder.
//
// Layout (top → bottom): Steps, Density, Brightness, and a Domain tab-strip
// footer. Volume is single-active: the domain *is* the rendering's identity, so
// the strip doubles as the on/off control. A tile glows (rail-style light) when
// its domain is rendering; clicking another tile switches the render, clicking
// the glowing tile turns volume off. There is no separate Enable checkbox.
//
// The domain strip is raw DOM appended to the folder body, so it survives
// pane child disposal between rebuilds. It is cached on the folder
// (mirroring Contour/Slices) and re-pinned to the body bottom on every rebuild.

import { store, params as storeParams } from '@store/instance';
import { INTENTS } from '@store/intents';
import { buildDomainStrip, renderDomainStrip, syncDomainSelected, type DomainStripRefs } from './domainStrip';
import { bindSlider } from '../sliderTicks';
import type { SelectionId } from '@store/schema';
import type { TPaneLike, TFolderLike, Refs } from '../types';

interface VolumeCtx {
    params: Record<string, unknown>;
    getVolumeUniformSetters?: () => { setSteps?: (v: number) => void; setDensity?: (v: number) => void; setBrightness?: (v: number) => void };
}

interface VolumeCustomRefs {
    domain: DomainStripRefs;
    offRefresh: () => void;
}

const CUSTOM_REFS_BY_FOLDER = new WeakMap<TFolderLike, VolumeCustomRefs>();

/** Single-active reconciliation: clicking a domain tile either toggles the
 *  volume off (the glowing tile) or switches/enables the render to that domain.
 *  Built from the two existing intents — order matters: set the domain first
 *  (a cheap no-op write while disabled) so the subsequent enable's `syncVolume`
 *  builds with the right clip. */
function activateVolumeDomain(id: SelectionId | 'full'): void {
    const on = !!storeParams.volumeEnabled;
    const active = String(storeParams.volumeDomain ?? 'full');
    if (on && id === active) {
        store.dispatch(INTENTS.TOGGLE_VOLUME, { enabled: false });
        return;
    }
    if (id !== active) {
        store.dispatch(INTENTS.SELECTION_SET_DOMAIN, { primitive: 'volume', selectionId: id });
    }
    if (!on) store.dispatch(INTENTS.TOGGLE_VOLUME, { enabled: true });
}

export function buildVolumeSection(paneArg: unknown, refs: Refs, ctx: VolumeCtx): Refs {
    const pane = paneArg as TPaneLike;
    const { params, getVolumeUniformSetters } = ctx;
    let folder = refs['volumeFolderRef'] as TFolderLike | undefined;
    if (!folder) {
        folder = pane.addFolder({ title: 'Volume Rendering', expanded: true });
        if (folder.element) folder.element.dataset['section'] = 'volume';
        refs['volumeFolderRef'] = folder;
    } else {
        try {
            const children = Array.isArray(folder.children) ? folder.children.slice() : [];
            children.forEach((child) => { try { (child as TFolderLike).dispose?.(); } catch { /* swallow */ } });
        } catch { /* swallow */ }
    }

    // Stale UI_PANE_REFRESH listener belongs to the prior build; drop it so the
    // new one owns the sync. The footer DOM (domain strip) is preserved.
    const existingRefs = CUSTOM_REFS_BY_FOLDER.get(folder);
    if (existingRefs) {
        try { existingRefs.offRefresh(); } catch { /* swallow */ }
    }

    const setters = typeof getVolumeUniformSetters === 'function' ? getVolumeUniformSetters() : {};
    bindSlider(folder, params, 'volumeSteps', { label: 'Steps', min: 32, max: 256, step: 32 }).on('change', (ev) => {
        try { if (setters.setSteps) setters.setSteps(ev.value as number); } catch { /* swallow */ }
    });
    bindSlider(folder, params, 'volumeDensity', { label: 'Density', min: 0.1, max: 20.0, step: 0.1 }).on('change', (ev) => {
        try { if (setters.setDensity) setters.setDensity(ev.value as number); } catch { /* swallow */ }
    });
    bindSlider(folder, params, 'volumeBrightness', { label: 'Brightness', min: 0.1, max: 3.0, step: 0.1 }).on('change', (ev) => {
        try { if (setters.setBrightness) setters.setBrightness(ev.value as number); } catch { /* swallow */ }
    });

    // Footer domain strip — the glow toggle. Preserved across rebuilds via the
    // WeakMap; the section registry fires a 'volume' rebuild on selection
    // add/delete/relabel, so we just re-render the tiles.
    const folderBodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (folderBodyEl) {
        folderBodyEl.querySelectorAll<HTMLElement>('.domain-strip').forEach((orphan) => {
            if (orphan !== existingRefs?.domain.container) orphan.remove();
        });
    }
    let domain = existingRefs?.domain;
    if (!domain) {
        domain = buildDomainStrip('volume', {
            showLight: true,
            activeGate: () => !!storeParams.volumeEnabled,
            onTileClick: activateVolumeDomain,
        });
    } else {
        renderDomainStrip(domain.stripEl, 'volume');
    }

    const offRefresh = store.onIntent(INTENTS.UI_PANE_REFRESH, () => { syncDomainSelected('volume'); });
    CUSTOM_REFS_BY_FOLDER.set(folder, { domain, offRefresh });

    const bodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (bodyEl) bodyEl.appendChild(domain.container);

    if (pane && pane.refresh) pane.refresh();
    return refs;
}
